import React, { useEffect, useState } from 'react';
import {
  Users,
  Mail,
  Shield,
  Plus,
  Trash2,
  UserPlus,
  X,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Modal } from '@/components/common/Modal';
import { Spinner } from '@/components/common/Spinner';
import { useTeamStore } from '@/store/teamStore';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';

type TabType = 'members' | 'invitations' | 'roles';

const ALL_PERMISSIONS = [
  'agents:read',
  'agents:write',
  'agents:delete',
  'conversations:read',
  'conversations:write',
  'knowledge_bases:read',
  'knowledge_bases:write',
  'channels:read',
  'channels:write',
  'settings:read',
  'settings:write',
  'team:read',
  'team:manage',
  'training:read',
  'training:write',
  'training:review',
  'skills:read',
  'skills:write',
  'exports:read',
  'exports:write',
  'billing:read',
  'billing:manage',
] as const;

const formatPermission = (perm: string): string => {
  const [resource, action] = perm.split(':');
  const formattedResource = resource.replace(/_/g, ' ');
  return `${formattedResource}: ${action}`;
};

const tabs: { id: TabType; label: string; icon: React.FC<{ className?: string }> }[] = [
  { id: 'members', label: 'Members', icon: Users },
  { id: 'invitations', label: 'Invitations', icon: Mail },
  { id: 'roles', label: 'Roles', icon: Shield },
];

export const TeamPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('members');

  const {
    members,
    membersLoading,
    fetchMembers,
    updateMemberRole,
    removeMember,
    invitations,
    invitationsLoading,
    fetchInvitations,
    inviteMember,
    revokeInvitation,
    myInvitations,
    fetchMyInvitations,
    acceptInvitation,
    declineInvitation,
    roles,
    rolesLoading,
    fetchRoles,
    createRole,
    updateRole,
    deleteRole,
  } = useTeamStore();

  const { user } = useAuthStore();

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [isInviting, setIsInviting] = useState(false);

  // Role modal state
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: {} as Record<string, boolean>,
  });
  const [isSavingRole, setIsSavingRole] = useState(false);

  // --- Data fetching per tab ---

  useEffect(() => {
    if (activeTab === 'members') {
      fetchMembers().catch(() => toast.error('Failed to load members'));
      fetchRoles().catch(() => {});
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'invitations') {
      fetchInvitations().catch(() => toast.error('Failed to load invitations'));
      fetchMyInvitations().catch(() => {});
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'roles') {
      fetchRoles().catch(() => toast.error('Failed to load roles'));
    }
  }, [activeTab]);

  // --- Members handlers ---

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await updateMemberRole(userId, newRole);
      toast.success('Member role updated');
    } catch {
      toast.error('Failed to update role');
    }
  };

  const handleRemoveMember = async (userId: string, name: string) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    try {
      await removeMember(userId);
      toast.success('Member removed');
    } catch {
      toast.error('Failed to remove member');
    }
  };

  // --- Invitation handlers ---

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setIsInviting(true);
    try {
      await inviteMember(inviteEmail.trim(), inviteRole);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('member');
      fetchInvitations().catch(() => {});
    } catch {
      toast.error('Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRevokeInvitation = async (id: string) => {
    if (!window.confirm('Revoke this invitation?')) return;
    try {
      await revokeInvitation(id);
      toast.success('Invitation revoked');
    } catch {
      toast.error('Failed to revoke invitation');
    }
  };

  const handleAcceptInvitation = async (token: string) => {
    try {
      await acceptInvitation(token);
      toast.success('Invitation accepted');
    } catch {
      toast.error('Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = async (token: string) => {
    try {
      await declineInvitation(token);
      toast.success('Invitation declined');
    } catch {
      toast.error('Failed to decline invitation');
    }
  };

  // --- Roles handlers ---

  const openRoleModal = (roleId?: string) => {
    if (roleId) {
      const role = roles.find((r) => r.id === roleId);
      if (!role) return;
      setEditingRoleId(roleId);
      setRoleForm({
        name: role.name,
        description: role.description || '',
        permissions: { ...role.permissions },
      });
    } else {
      setEditingRoleId(null);
      setRoleForm({ name: '', description: '', permissions: {} });
    }
    setIsRoleModalOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    setIsSavingRole(true);
    try {
      if (editingRoleId) {
        await updateRole(editingRoleId, {
          name: roleForm.name,
          description: roleForm.description || undefined,
          permissions: roleForm.permissions,
        });
        toast.success('Role updated');
      } else {
        await createRole({
          name: roleForm.name,
          description: roleForm.description || undefined,
          permissions: roleForm.permissions,
        });
        toast.success('Role created');
      }
      setIsRoleModalOpen(false);
    } catch {
      toast.error('Failed to save role');
    } finally {
      setIsSavingRole(false);
    }
  };

  const handleDeleteRole = async (id: string) => {
    if (!window.confirm('Delete this role? Members using it will revert to default.')) return;
    try {
      await deleteRole(id);
      toast.success('Role deleted');
    } catch {
      toast.error('Failed to delete role');
    }
  };

  const togglePermission = (perm: string) => {
    setRoleForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [perm]: !prev.permissions[perm],
      },
    }));
  };

  // --- Status badge variant ---

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'declined':
      case 'revoked':
        return 'error';
      case 'expired':
        return 'default';
      default:
        return 'default';
    }
  };

  // --- Render ---

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Team</h1>
          <p className="text-muted-foreground mt-1">Manage members, invitations, and roles</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b-2 border-border">
        <nav className="flex gap-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 border-b-2 transition-colors',
                  activeTab === tab.id
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {/* =========== Members Tab =========== */}
        {activeTab === 'members' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-gray-900">Team Members</CardTitle>
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" />
                </div>
              ) : members.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No team members found</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {members.map((member) => {
                    const isSelf = user?.id === member.user_id;
                    const initial = (member.user_name || member.user_email || '?')
                      .charAt(0)
                      .toUpperCase();

                    return (
                      <div
                        key={member.id}
                        className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-500 text-white border-2 border-[#1a1a2e] flex items-center justify-center font-semibold text-sm">
                            {initial}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">
                              {member.user_name}
                              {isSelf && (
                                <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">
                              {member.user_email}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0">
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.user_id, e.target.value)}
                            disabled={isSelf && member.role === 'owner'}
                            className={cn(
                              'flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                              isSelf && member.role === 'owner' && 'opacity-50 cursor-not-allowed'
                            )}
                          >
                            <option value="owner">Owner</option>
                            <option value="admin">Admin</option>
                            <option value="member">Member</option>
                            <option value="viewer">Viewer</option>
                          </select>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleRemoveMember(member.user_id, member.user_name)
                            }
                            disabled={isSelf}
                            title={isSelf ? 'You cannot remove yourself' : 'Remove member'}
                          >
                            <Trash2
                              className={cn(
                                'h-4 w-4',
                                isSelf ? 'text-muted-foreground' : 'text-destructive'
                              )}
                            />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* =========== Invitations Tab =========== */}
        {activeTab === 'invitations' && (
          <div className="space-y-6">
            {/* Invite Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-gray-900">Invite a Team Member</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendInvite} className="flex items-end gap-3">
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                  />
                  <div className="w-full max-w-[160px]">
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </div>
                  <Button type="submit" isLoading={isInviting} className="flex-shrink-0">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Send Invite
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Pending Invitations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-gray-900">Pending Invitations</CardTitle>
              </CardHeader>
              <CardContent>
                {invitationsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Spinner size="lg" />
                  </div>
                ) : invitations.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No pending invitations</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {invitations.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">
                            {inv.email}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline">{inv.role}</Badge>
                            <Badge variant={getStatusBadgeVariant(inv.status)}>
                              {inv.status}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Expires {new Date(inv.expires_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        {inv.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokeInvitation(inv.id)}
                          >
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Invitations */}
            {myInvitations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-gray-900">Invitations for You</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myInvitations.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-border bg-gray-50"
                      >
                        <div>
                          <p className="font-medium text-sm text-gray-900">
                            {inv.organization_name && (
                              <span className="font-semibold">{inv.organization_name}</span>
                            )}
                            {inv.organization_name ? ' — ' : ''}
                            Invited as <span className="capitalize font-semibold">{inv.role}</span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Expires {new Date(inv.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAcceptInvitation(inv.token)}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeclineInvitation(inv.token)}
                          >
                            Decline
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* =========== Roles Tab =========== */}
        {activeTab === 'roles' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div />
              <Button onClick={() => openRoleModal()} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Create Role
              </Button>
            </div>

            {rolesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : roles.length === 0 ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-12">
                    <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No custom roles defined</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Create roles to assign granular permissions to team members
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {roles.map((role) => {
                  const enabledPerms = Object.entries(role.permissions)
                    .filter(([, v]) => v)
                    .map(([k]) => k);

                  return (
                    <Card key={role.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg text-gray-900">
                              {role.name}
                              {role.is_system && (
                                <Badge variant="info" className="ml-2 text-xs">
                                  System
                                </Badge>
                              )}
                            </CardTitle>
                            {role.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {role.description}
                              </p>
                            )}
                          </div>
                          {!role.is_system && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openRoleModal(role.id)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRole(role.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {enabledPerms.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No permissions assigned</p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {enabledPerms.map((perm) => (
                              <Badge key={perm} variant="outline" className="text-xs">
                                {formatPermission(perm)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* =========== Create / Edit Role Modal =========== */}
      <Modal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        size="xl"
      >
        <div className="rounded-xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {editingRoleId ? 'Edit Role' : 'Create Role'}
          </h2>

          <div className="space-y-4">
            <Input
              label="Role Name"
              value={roleForm.name}
              onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
              placeholder="e.g., Support Agent"
              required
            />

            <Input
              label="Description"
              value={roleForm.description}
              onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
              placeholder="Brief description of this role"
            />

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Permissions
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto p-1">
                {ALL_PERMISSIONS.map((perm) => (
                  <label
                    key={perm}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm',
                      roleForm.permissions[perm]
                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                        : 'border-border hover:border-blue-300'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={!!roleForm.permissions[perm]}
                      onChange={() => togglePermission(perm)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="capitalize">{formatPermission(perm)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t">
              <Button onClick={handleSaveRole} isLoading={isSavingRole}>
                {isSavingRole ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                {editingRoleId ? 'Update Role' : 'Create Role'}
              </Button>
              <Button onClick={() => setIsRoleModalOpen(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
